import { Grid, GridItem, Text } from "@chakra-ui/react";
import { Fragment, useMemo } from "react";

import { AdjustableArrow } from "@/common/components/AdjustableArrow";
import { RulesChangesEstimation } from "@/common/components/Rule/RulesChangesEstimation";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { EnvironmentDisplayName } from "@/environment/components/EnvironmentDisplayName";
import { FormFeatureStagePicker } from "@/feature/components/Form/FormFeatureStagePicker";
import { hasTargetingChanged } from "@/feature/utils/targetingDiff";
import { StageDisplayName } from "@/stage/components/StageDisplayName";
import { FeatureDetail } from "@reflag/shared/featureAPI";
import { UpdateEnvironmentFeatureTargeting } from "@reflag/shared/featureTargetingAPI";
import { StageDTO } from "@reflag/shared/stageAPI";
import { filterMap } from "@reflag/shared/utils/array";

type FeatureTargetingProposedChangesProps = {
  feature: FeatureDetail;
  stages: StageDTO[];
  newStageId: string | null;
  currentTargeting: UpdateEnvironmentFeatureTargeting[];
  newTargeting: UpdateEnvironmentFeatureTargeting[];
  targetingSnapshot: UpdateEnvironmentFeatureTargeting[];
  canChangeStage: boolean;
  onStageChange?: (stageId: string) => void;
};

export function FeatureTargetingProposedChanges({
  feature,
  stages,
  newStageId,
  currentTargeting,
  newTargeting,
  targetingSnapshot,
  canChangeStage,
  onStageChange,
}: FeatureTargetingProposedChangesProps) {
  const { app } = useEnvironment();
  const changedPairs = useMemo(
    () =>
      filterMap(newTargeting, (newVersion) => {
        const oldVersion = currentTargeting.find(
          ({ environmentId }) => environmentId === newVersion.environmentId,
        );

        if (!oldVersion) {
          return undefined;
        }

        const pair = {
          newVersion,
          currentVersion: oldVersion,
        };

        return hasTargetingChanged(newVersion, oldVersion) ? pair : undefined;
      }),
    [currentTargeting, newTargeting],
  );

  const currentStage = stages.find((stage) => stage.id === feature.stage?.id)!;
  const newStage = stages.find((stage) => stage.id === newStageId)!;

  const arrowCell = (
    <GridItem alignSelf="center" justifySelf="center">
      <AdjustableArrow color="dimmed" length={32} />
    </GridItem>
  );

  const rows = changedPairs.map(({ newVersion, currentVersion }) => {
    const envId = currentVersion.environmentId;
    const environment = app.environments.find(
      (environment) => environment.id === envId,
    );

    return (
      <Fragment key={newVersion.environmentId}>
        <GridItem>
          {environment && <EnvironmentDisplayName environment={environment} />}
        </GridItem>
        <GridItem>
          <RulesChangesEstimation
            alignItems="flex-start"
            currentTargeting={currentVersion}
            flagKey={feature.key}
            flexDirection="column"
          />
        </GridItem>
        {arrowCell}
        <GridItem colSpan={2}>
          <RulesChangesEstimation
            alignItems="flex-start"
            currentTargeting={currentVersion}
            flagKey={feature.key}
            flexDirection="column"
            newTargeting={newVersion}
          />
        </GridItem>
      </Fragment>
    );
  });

  return (
    <Grid
      backgroundColor="appBackdrop"
      borderColor="appBorder"
      borderStyle="solid"
      borderWidth="1px 0px"
      gap={6}
      px={8}
      py={4}
      templateColumns="128px 176px min-content 176px auto"
      w="full"
      whiteSpace="nowrap"
    >
      <GridItem></GridItem>
      <GridItem
        alignItems="flex-start"
        display="flex"
        flexDirection="column"
        justifyContent="center"
      >
        <StageDisplayName stage={currentStage} w="full" />
      </GridItem>
      {arrowCell}
      <GridItem
        alignItems="flex-start"
        display="flex"
        flexDirection="column"
        justifyContent="center"
      >
        {canChangeStage ? (
          <FormFeatureStagePicker
            feature={feature}
            onStageChange={onStageChange}
            stageId={newStageId}
            targetingSnapshot={targetingSnapshot}
          />
        ) : (
          <StageDisplayName stage={newStage} w="full" />
        )}
      </GridItem>
      <GridItem></GridItem>
      {rows.length > 0 ? (
        rows
      ) : (
        <GridItem colSpan={5} textAlign="center">
          <Text color="dimmed">No targeting changes will be made.</Text>
        </GridItem>
      )}
    </Grid>
  );
}
